import type { Project } from '@ai-studio/shared';
import { useEffect, useState, type ReactNode } from 'react';

import { ApiError, api } from '../lib/api.js';
import {
  embedParams,
  postToHost,
  readTabMessage,
  tabCycleDelta,
  type HostMessage,
} from '../lib/embed.js';
import { runWorkspaceMenuCommand } from '../lib/menu-commands.js';
import { eventSocket } from '../lib/ws.js';
import { useAuth } from '../state/auth.js';
import { useModel } from '../state/model.js';
import { useRig } from '../state/rig.js';
import { usePixel } from '../state/pixel.js';
import { useScene } from '../state/scene.js';
import { useWorkspace } from '../state/workspace.js';
import { Workspace } from './Workspace.js';
import { useFollow } from '../state/follow.js';

/**
 * One project tab: the app inside a same-origin iframe (`?embedded=1&project=<id>`).
 * No title bar and no project list -- just the workspace, filling the frame.
 * The session cookie is shared with the host, so auth bootstraps on its own;
 * if it fails the host is told to re-check and show login.
 */
export function EmbeddedApp() {
  const status = useAuth((s) => s.status);
  const bootstrap = useAuth((s) => s.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    if (status === 'unauthenticated' || status === 'unconfigured')
      postToHost({ type: 'auth-lost' });
  }, [status]);

  useTabShortcuts();
  useHostMessages();

  if (status === 'loading') {
    return (
      <div className="boot">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  if (status !== 'authenticated') {
    return (
      <EmbedNotice message="This tab's session has ended.">
        <button type="button" className="primary" onClick={() => window.location.reload()}>
          Reload
        </button>
      </EmbedNotice>
    );
  }

  return <EmbeddedWorkspace projectId={embedParams().projectId} />;
}

function EmbedNotice({ message, children }: { message: string; children?: ReactNode }) {
  return (
    <div className="boot embed-notice">
      <p className="muted">{message}</p>
      <div className="embed-notice-actions">{children}</div>
    </div>
  );
}

/** Saves still in flight or failed, or code buffers that differ from disk. */
function hasUnsavedWork(): boolean {
  if (useWorkspace.getState().anyDirty()) return true;
  const rig = useRig.getState();
  if (rig.path && (rig.saving || JSON.stringify(rig.rig) !== rig.saved)) return true;
  return [
    useScene.getState().saveState,
    useModel.getState().saveState,
    usePixel.getState().saveState,
  ].some((state) => state !== 'saved');
}

async function flushDocuments(): Promise<void> {
  await Promise.allSettled([
    useScene.getState().flush(),
    useModel.getState().flush(),
    usePixel.getState().flush(),
  ]);
}

/** Tab-cycling chords must reach the host even while focus is inside this frame. */
function useTabShortcuts(): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const delta = tabCycleDelta(event);
      if (!delta) return;
      event.preventDefault();
      event.stopPropagation();
      postToHost({ type: 'cycle', delta });
    };
    // Capture phase, so Monaco and the terminal never see the chord.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
}

/** Requests from the host. Mounted for the whole frame so a close always gets an answer. */
function useHostMessages(): void {
  useEffect(() => {
    const origin = window.location.origin;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const message = readTabMessage<HostMessage>(event, origin);
      if (!message) return;
      switch (message.type) {
        case 'prepare-close':
          void flushDocuments().then(() =>
            postToHost({ type: 'close-ready', dirty: hasUnsavedWork() }),
          );
          break;
        case 'menu':
          runWorkspaceMenuCommand(String(message.command));
          break;
        case 'activated':
          // Views measured while hidden re-fit to the now-visible frame.
          window.dispatchEvent(new Event('resize'));
          useFollow.getState().setVisible(true);
          break;
        case 'deactivated':
          useFollow.getState().setVisible(false);
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
}

function EmbeddedWorkspace({ projectId }: { projectId: number | null }) {
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  const openId = useWorkspace((s) => s.projectId);

  // The frame owns its own event socket, scoped to this project by Workspace.
  useEffect(() => {
    eventSocket.connect(null);
    return () => eventSocket.close();
  }, []);

  useEffect(() => {
    if (projectId === null) {
      setError('No project was given for this tab.');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const result = await api.get<{ projects: Project[] }>('/projects');
        if (cancelled) return;
        const found = result.projects.find((p) => p.id === projectId);
        if (!found) {
          setError('This project no longer exists.');
          return;
        }
        setProject(found);
        await useWorkspace.getState().openProject(found.id, found.name);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : 'Could not load the project.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Tell the host the tab label and whether anything is unsaved.
  useEffect(() => {
    if (!project) return;
    let last = '';
    const report = () => {
      const dirty = hasUnsavedWork();
      const key = `${project.name}\u0000${dirty}`;
      if (key === last) return;
      last = key;
      postToHost({ type: 'state', name: project.name, dirty });
    };
    report();
    const offs = [
      useWorkspace.subscribe(report),
      useScene.subscribe(report),
      useModel.subscribe(report),
      useRig.subscribe(report),
      usePixel.subscribe(report),
    ];
    return () => offs.forEach((off) => off());
  }, [project]);

  if (error) {
    return (
      <EmbedNotice message={error}>
        <button type="button" className="ghost" onClick={() => postToHost({ type: 'close' })}>
          Close tab
        </button>
      </EmbedNotice>
    );
  }

  if (!project || openId !== project.id) {
    return (
      <div className="boot">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="app-root">
      <div className="shell">
        <Workspace project={project} />
      </div>
    </div>
  );
}
